"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import {
  Search,
  X,
  ChevronRight,
  Loader2,
  ArrowLeft,
  Image as ImageIcon,
  SearchX,
  TrendingUp,
  History
} from "lucide-react";
import { API_UPLOAD_URL, API_BASE_URL, getImageUrl } from '@/app/lib/api';

interface Product {
  id: string;
  name: string;
  slug: string;
  images: string[];
  price: number;
  promoPrice?: number | null;
  categoryName?: string;
}

interface RechercheProps {
  onClose?: () => void;
}

export default function Recherche({ onClose }: RechercheProps) {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/products/search?q=${encodeURIComponent(query)}`);
        if (res.ok) {
          const data = await res.json();
          const cleanData = (Array.isArray(data) ? data : data.items || []).map((p: any) => ({
            id: p.id,
            name: p.name,
            slug: p.slug,
            images: Array.isArray(p.images) ? p.images : [p.image].filter(Boolean),
            price: Number(p.price),
            promoPrice: p.promoPrice ? Number(p.promoPrice) : null,
            categoryName: p.category?.name
          }));
          setResults(cleanData);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [query]);

  const formatPrice = (p: number) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "TND",
      minimumFractionDigits: 3
    }).format(p).replace("TND", "DT");

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-start sm:pt-[5vh]">
      {/* Background Overlay */}
      <div
        className="absolute inset-0 bg-slate-950/40 backdrop-blur-xl animate-in fade-in duration-500"
        onClick={onClose}
      />

      {/* Search Container */}
      <div
        className={`
          relative w-full max-w-3xl bg-white flex flex-col overflow-hidden
          transition-all duration-500 ease-out
          sm:rounded-3xl sm:shadow-[0_20px_70px_-10px_rgba(0,0,0,0.3)]
          h-full sm:h-auto sm:max-h-[80vh]
          animate-in slide-in-from-bottom-8
        `}
      >
        {/* Search Header */}
        <div className="relative flex items-center gap-2 p-4 border-b border-slate-100">
          <button
            onClick={onClose}
            className="sm:hidden p-2 hover:bg-slate-100 rounded-full text-slate-500"
          >
            <ArrowLeft size={24} />
          </button>

          <div className="relative flex-1 group">
            <Search
              size={20}
              className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${isLoading ? "text-[#a81b8d] animate-pulse" : "text-slate-400 group-focus-within:text-[#a81b8d]"
                }`}
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && query.trim().length >= 2) {
                  router.push(`/nos-produits?q=${encodeURIComponent(query.trim())}`);
                  onClose?.();
                }
              }}
              placeholder="Rechercher un produit, une référence..."
              className="w-full bg-slate-50 border-2 border-transparent focus:border-[#a81b8d]/10 focus:bg-white text-slate-900 rounded-2xl py-3.5 pl-12 pr-12 transition-all outline-none text-base font-medium"
            />
            {query && !isLoading && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded-full transition-all"
              >
                <X size={14} />
              </button>
            )}
            {isLoading && (
              <Loader2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-[#a81b8d]" />
            )}
          </div>

          <button
            onClick={onClose}
            className="hidden sm:flex p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar bg-white">
          {query.length < 2 ? (
            <div className="p-8 animate-in fade-in duration-500">
              <div className="mb-8">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <TrendingUp size={14} /> Suggestions
                </h3>
                <div className="flex flex-wrap gap-2">
                  {["Brosse pour vaisselle", "Eponges", "cuisson"].map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setQuery(tag)}
                      className="px-4 py-2 bg-slate-50 hover:bg-[#a81b8d]/5 hover:text-[#a81b8d] border border-slate-100 rounded-xl text-sm font-medium transition-all"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4 rotate-3">
                  <Search size={32} className="text-slate-200" />
                </div>
                <p className="text-slate-500 text-sm max-w-[200px]">Tapez au moins 2 caractères pour lancer la recherche.</p>
              </div>
            </div>
          ) : results.length === 0 && !isLoading ? (
            <div className="py-20 flex flex-col items-center text-center animate-in zoom-in-95 duration-300">
              <div className="w-20 h-20 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mb-4">
                <SearchX size={40} />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Aucun résultat</h3>
              <p className="text-slate-500 text-sm mt-1">
                Aucun produit ne correspond à "<span className="text-slate-900 font-semibold">{query}</span>"
              </p>
            </div>
          ) : (
            <div className="p-2 sm:p-4 space-y-2">
              {results.map((product, index) => (
                <Link
                  key={product.id}
                  href={`/produit/${product.slug}`}
                  onClick={onClose}
                  style={{ animationDelay: `${index * 50}ms` }}
                  className="group flex items-center gap-4 p-3 rounded-2xl hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-all animate-in slide-in-from-bottom-2"
                >
                  {/* Product Image */}
                  <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl bg-slate-50 border border-slate-100">
                    {product.images?.[0] ? (
                      <Image
                        src={getImageUrl(product.images[0])}
                        alt={product.name}
                        fill
                        className="object-contain p-2 group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-slate-300">
                        <ImageIcon size={24} />
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    {product.categoryName && (
                      <span className="text-[10px] font-bold text-[#a81b8d] uppercase tracking-wider mb-1 block">
                        {product.categoryName}
                      </span>
                    )}
                    <h4 className="text-sm sm:text-base font-bold text-slate-800 truncate group-hover:text-[#a81b8d] transition-colors leading-tight">
                      {product.name}
                    </h4>

                    <div className="flex items-center gap-3 mt-2">
                      {product.promoPrice ? (
                        <>
                          <span className="text-base font-black text-[#a81b8d]">
                            {formatPrice(product.promoPrice)}
                          </span>
                          <span className="text-xs text-slate-400 line-through decoration-red-400">
                            {formatPrice(product.price)}
                          </span>
                          <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase">
                            Promo
                          </span>
                        </>
                      ) : (
                        <span className="text-base font-bold text-slate-900">
                          {formatPrice(product.price)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 text-slate-400 group-hover:bg-[#a81b8d] group-hover:text-white transition-all transform group-hover:translate-x-1">
                    <ChevronRight size={20} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Search Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded shadow-sm text-slate-500">ESC</kbd> Fermer
            </span>
            <span className="hidden sm:flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded shadow-sm text-slate-500">↵</kbd> Choisir
            </span>
          </div>
          {results.length > 0 && (
            <span className="font-bold text-[#a81b8d]/70 uppercase tracking-widest italic">
              GEI Power Search
            </span>
          )}
        </div>
      </div>
    </div>
  );
}